import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useSupportConversation } from '@/hooks/useSupportConversation';
import { useSupportConversationPresence } from '@/hooks/useSupportConversationPresence';
import { useSupportMessages } from '@/hooks/useSupportMessages';
import { useIsMobile } from '@/hooks/use-mobile';

import {
  removeSupportAttachment,
  revokeSupportAttachmentPreview,
  uploadSupportAttachment,
} from '@/lib/supportAttachments';
import {
  createOptimisticSupportMessage,
  sendSupportMessage,
  sortSupportMessages,
  type SupportMessageLike,
} from '@/lib/supportChat';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { SupportMessageComposer, type SupportMessageComposerSubmission } from './SupportMessageComposer';
import { SupportMessageList } from './SupportMessageList';
import { WhatsAppIcon } from './WhatsAppIcon';
import { X } from 'lucide-react';

interface WhatsAppSupportSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DESKTOP_PANEL_WIDTH = 520;

export function WhatsAppSupportSheet({ open, onOpenChange }: WhatsAppSupportSheetProps) {
  const { user } = useAuth();
  const { profile } = useProfile();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { toast } = useToast();
  const messageListRef = useRef<HTMLDivElement | null>(null);
  const [sending, setSending] = useState(false);
  const [optimisticMessages, setOptimisticMessages] = useState<SupportMessageLike[]>([]);
  const [mounted, setMounted] = useState(false);
  const { conversation, loading: conversationLoading, error: conversationError, refreshConversation, setConversation } = useSupportConversation({
    enabled: open && Boolean(user),
  });
  const {
    messages,
    loading: messagesLoading,
    error: messagesError,
    upsertMessage,
    refreshMessages,
  } = useSupportMessages({
    conversationId: conversation?.id,
    enabled: open && Boolean(user),
  });

  useSupportConversationPresence({
    conversationId: conversation?.id,
    enabled: open && Boolean(user),
    trackSelf: true,
  });

  const displayedMessages = useMemo(() => sortSupportMessages([...messages, ...optimisticMessages]), [messages, optimisticMessages]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onOpenChange(false);
      }
    };

    window.addEventListener('keydown', handleEscape);

    if (isMobile) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = previousOverflow;
        window.removeEventListener('keydown', handleEscape);
      };
    }

    const root = document.getElementById('root');
    const previousPaddingRight = root?.style.paddingRight || '';
    const previousTransition = root?.style.transition || '';

    if (root) {
      root.style.transition = previousTransition ? `${previousTransition}, padding-right 220ms ease` : 'padding-right 220ms ease';
      root.style.paddingRight = `${DESKTOP_PANEL_WIDTH}px`;
    }

    return () => {
      if (root) {
        root.style.paddingRight = previousPaddingRight;
        root.style.transition = previousTransition;
      }
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isMobile, onOpenChange, open]);

  const scrollMessagesToBottom = () => {
    const viewport = messageListRef.current;
    if (!viewport) {
      return;
    }

    viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'smooth' });
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    scrollMessagesToBottom();
  }, [displayedMessages, open]);

  useEffect(() => {
    if (!open) {
      setOptimisticMessages([]);
      setSending(false);
    }
  }, [open]);

  const handleRetry = async () => {
    const nextConversation = await refreshConversation();
    if (nextConversation && nextConversation.id) {
      await refreshMessages();
    }
  };

  const handleLoginRedirect = () => {
    onOpenChange(false);
    navigate('/auth');
  };

  const handleSend = async ({ body, attachment }: SupportMessageComposerSubmission) => {
    if (!user) {
      return false;
    }

    const optimisticPreviewUrl = attachment?.type.startsWith('image/') ? URL.createObjectURL(attachment) : null;
    const optimisticMessage = createOptimisticSupportMessage(body, conversation?.id, {
      mediaUrl: optimisticPreviewUrl,
      mediaType: attachment?.type || null,
      metadata: attachment
        ? {
            attachment_bucket: 'support-attachments',
            attachment_path: `pending/${attachment.name}`,
            attachment_name: attachment.name,
            attachment_size: attachment.size,
          }
        : {},
    });

    setOptimisticMessages((current) => [...current, optimisticMessage]);
    setSending(true);

    let uploadedAttachment: Awaited<ReturnType<typeof uploadSupportAttachment>> | null = null;

    try {
      if (attachment) {
        uploadedAttachment = await uploadSupportAttachment(attachment, user.id);
      }

      const result = await sendSupportMessage(body, uploadedAttachment);
      setOptimisticMessages((current) => current.filter((message) => message.id !== optimisticMessage.id));
      setConversation(result.conversation);
      upsertMessage(result.message);

      if (!result.ok) {
        toast({
          title: 'Message not delivered',
          description: result.error || 'Your message was saved, but WhatsApp delivery failed.',
          variant: 'destructive',
        });
      }

      return true;
    } catch (error) {
      setOptimisticMessages((current) => current.filter((message) => message.id !== optimisticMessage.id));

      if (uploadedAttachment) {
        void removeSupportAttachment(uploadedAttachment.path).catch(() => undefined);
      }

      toast({
        title: 'Could not send message',
        description: error instanceof Error ? error.message : 'Please try again in a moment.',
        variant: 'destructive',
      });
      return false;
    } finally {
      revokeSupportAttachmentPreview(optimisticPreviewUrl);
      setSending(false);
    }
  };

  if (!mounted || !open) {
    return null;
  }

  const panel = (
    <aside
      className={isMobile ? 'absolute inset-y-0 right-0 flex h-full w-full max-w-xl animate-in slide-in-from-right duration-300' : 'fixed inset-y-0 right-0 z-[260] flex h-screen w-full max-w-[520px] border-l border-border/70 bg-background shadow-2xl'}
      style={isMobile ? undefined : { width: `${DESKTOP_PANEL_WIDTH}px` }}
    >
      <div className="flex h-full w-full flex-col bg-background">
        <div className="border-b border-border/70 px-5 py-5 text-left">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3 pr-2">
              <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                <WhatsAppIcon className="h-6 w-6" />
              </div>
              <div className="min-w-0 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-semibold text-foreground">Chat with our Support Team</h2>
                  <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
                    With WhatsApp Business Support
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  We will reply to you as soon as possible between Monday-Friday, 9-5PM Atlantic. Except Holidays.
                </p>
                {conversation ? <p className="text-xs text-muted-foreground">Conversation #{conversation.site_conversation_ref}</p> : null}
              </div>
            </div>

            <Button type="button" variant="ghost" size="icon" onClick={() => onOpenChange(false)} aria-label="Close support panel">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {!user ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-foreground">Log in to message support</h3>
              <p className="text-sm text-muted-foreground">The support inbox is available for authenticated users only.</p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button type="button" onClick={handleLoginRedirect} className="bg-emerald-600 hover:bg-emerald-700">
                Go to login
              </Button>
              <Button type="button" variant="outline" asChild>
                <Link to="/enterprise" onClick={() => onOpenChange(false)}>
                  Contact sales instead
                </Link>
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div ref={messageListRef} className="flex-1 overflow-y-auto bg-muted/10 px-4 py-4">
              <SupportMessageList
                messages={displayedMessages}
                loading={conversationLoading || (Boolean(conversation) && messagesLoading)}
                error={conversationError || messagesError}
                onRetry={() => {
                  void handleRetry();
                }}
                onMediaLoad={scrollMessagesToBottom}
                currentUserLabel={profile?.name || user?.email?.split('@')[0] || 'You'}
                otherUserLabel="Support Team"
              />

            </div>
            <SupportMessageComposer
              disabled={conversationLoading}
              sending={sending}
              onSend={handleSend}
              helperText="You can attach images, TXT, MD, CSV, or JSON files up to 5MB."
              allowAttachments
            />
          </>
        )}
      </div>
    </aside>
  );

  if (isMobile) {
    return createPortal(
      <div className="fixed inset-0 z-[260]">
        <button
          type="button"
          aria-label="Close support panel"
          className="absolute inset-0 bg-black/55 backdrop-blur-[2px] animate-in fade-in-0 duration-200"
          onClick={() => onOpenChange(false)}
        />
        {panel}
      </div>,
      document.body
    );
  }

  return createPortal(panel, document.body);
}
